import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Camera } from 'lucide-react';
import type { Profile } from '../types/database';
import { cn } from '../lib/utils';
import { compressImage } from '../lib/mediaCompression';
import ImageCropper from '../components/ImageCropper';
import getCroppedImg from '../lib/cropImage';

export default function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) {
        navigate('/login');
        return;
      }

      // Set email from auth user
      setEmail(user.email || '');

      // Get profile data
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (profile) {
        setProfile(profile);
        setFullName(profile.full_name || '');
        // Convert YYYY-MM-DD to MM/DD/YYYY
        if (profile.birthday) {
          const [year, month, day] = profile.birthday.split('-');
          setBirthday(`${month}/${day}/${year}`);
        } else {
          setBirthday('');
        }
      }
    } catch (error: any) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const validateBirthday = (date: string): boolean => {
    // Convert from MM/DD/YYYY to YYYY-MM-DD for Date object
    const [month, day, year] = date.split('/');
    const birthDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    const minDate = new Date('1900-01-01');
    const age = today.getFullYear() - birthDate.getFullYear();
    
    // Check if date is valid
    if (isNaN(birthDate.getTime())) {
      return false;
    }
    
    // Check if date is within valid range
    if (birthDate > today || birthDate < minDate) {
      return false;
    }
    
    // Check if user is at least 13 years old
    if (age < 13) {
      return false;
    }
    
    return true;
  };

  const handleBirthdayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    
    // Allow backspace and delete
    if (input.length < birthday.length) {
      setBirthday(input);
      return;
    }

    // Format the date as the user types
    if (input.length <= 10) {
      let formattedDate = input;
      
      // Add forward slash after month
      if (input.length === 2) {
        formattedDate += '/';
      }
      // Add forward slash after day
      else if (input.length === 5) {
        formattedDate += '/';
      }
      
      setBirthday(formattedDate);
    }
  };

  const updateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      if (!profile?.id) throw new Error('No profile found');

      // Validate full name
      if (!fullName.trim()) {
        throw new Error('Full name is required');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please enter a valid email address');
      }

      // Validate birthday if provided
      if (birthday && !validateBirthday(birthday)) {
        throw new Error('Please enter a valid birth date (You must be at least 13 years old)');
      }

      // Get current user's email
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('No user found');

      // Update email if changed
      if (email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: email
        });

        if (emailError) throw emailError;
      }

      // Convert MM/DD/YYYY to YYYY-MM-DD for database storage
      let formattedBirthday = null;
      if (birthday) {
        const [month, day, year] = birthday.split('/');
        formattedBirthday = `${year}-${month}-${day}`;
      }

      // Update profile data
      const updates = {
        full_name: fullName,
        birthday: formattedBirthday,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile.id);

      if (error) throw error;

      setMessage(email !== user.email 
        ? 'Profile updated successfully. Please check your email to confirm your new email address.'
        : 'Profile updated successfully');
      
      // Update local state
      setProfile(prev => prev ? { ...prev, ...updates } : null);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setPasswordLoading(true);
      setPasswordError(null);
      setPasswordMessage(null);

      // Validate passwords
      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (newPassword.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      // Clear password fields
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage('Password updated successfully');
    } catch (error: any) {
      console.error('Error updating password:', error);
      setPasswordError(error.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleAvatarSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
      }

      // Create preview URL for cropping
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
      const previewUrl = URL.createObjectURL(file);
      
      // Set original image for cropping
      setOriginalImage(previewUrl);
      setShowCropper(true);
      setError(null);
    } catch (err: any) {
      console.error('Error processing avatar:', err);
      setError(err.message || 'Failed to process image. Please try another file.');
    }
  };

  const handleCropComplete = async (croppedAreaPixels: any) => {
    if (!originalImage) return;
    
    try {
      setUploading(true);
      
      // Get the cropped image as a blob
      const croppedImageBlob = await getCroppedImg(originalImage, croppedAreaPixels);
      
      // Convert blob to file
      const croppedFile = new File([croppedImageBlob], 'cropped-avatar.jpg', { 
        type: 'image/jpeg' 
      });
      
      // Process the file (compress if needed)
      let processedFile = croppedFile;
      if (croppedFile.size > 2 * 1024 * 1024) { // If larger than 2MB
        processedFile = await compressImage(croppedFile);
      }
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Upload the cropped image
      const fileExt = 'jpg'; // Always jpg for cropped images
      const filePath = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, processedFile, {
          contentType: 'image/jpeg',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Update local state
      setProfile(prev => prev ? { ...prev, avatar_url: publicUrl } : null);
      setMessage('Avatar updated successfully');
      
      // Create preview URL for the uploaded image
      const previewUrl = URL.createObjectURL(processedFile);
      setAvatarPreview(previewUrl);
      
      // Close cropper and clean up
      setShowCropper(false);
      if (originalImage) {
        URL.revokeObjectURL(originalImage);
        setOriginalImage(null);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    if (originalImage) {
      URL.revokeObjectURL(originalImage);
      setOriginalImage(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">Profile Settings</h1>

        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className="relative">
              <img
                src={avatarPreview || profile?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${fullName}`}
                alt="Avatar"
                className="h-24 w-24 rounded-full object-cover"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${fullName}`;
                }}
              />
              <label
                htmlFor="avatar-upload"
                className={cn(
                  "absolute bottom-0 right-0 p-2 rounded-full cursor-pointer",
                  "bg-blue-500 hover:bg-blue-600 transition-colors",
                  uploading && "opacity-50 cursor-not-allowed"
                )}
              >
                <Camera className="h-4 w-4 text-white" />
                <input
                  type="file"
                  id="avatar-upload"
                  accept="image/*"
                  onChange={handleAvatarSelect}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
            </div>
          </div>
          {uploading && (
            <p className="text-center text-sm text-gray-500 mt-2">Uploading...</p>
          )}
        </div>

        <form onSubmit={updateProfile} className="space-y-6">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <input
              type="text"
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="birthday" className="block text-sm font-medium text-gray-700">
              Birthday (MM/DD/YYYY)
            </label>
            <input
              type="text"
              id="birthday"
              placeholder="MM/DD/YYYY"
              pattern="\d{2}/\d{2}/\d{4}"
              value={birthday}
              onChange={handleBirthdayChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              You must be at least 13 years old
            </p>
          </div>

          {error && (
            <div className="text-red-500 text-sm">{error}</div>
          )}

          {message && (
            <div className="text-green-500 text-sm">{message}</div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className={cn(
                "px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Password Change Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-6">Change Password</h2>
        <form onSubmit={updatePassword} className="space-y-6">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={6}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={6}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          {passwordError && (
            <div className="text-red-500 text-sm">{passwordError}</div>
          )}

          {passwordMessage && (
            <div className="text-green-500 text-sm">{passwordMessage}</div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={passwordLoading}
              className={cn(
                "px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>

      {showCropper && originalImage && (
        <ImageCropper
          image={originalImage}
          onCropComplete={handleCropComplete}
          onCancel={handleCancelCrop}
          aspectRatio={1}
        />
      )}
    </div>
  );
}